import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	inject,
	NgZone,
	output,
	Renderer2,
	signal,
	viewChild,
} from '@angular/core';

type DrawerPlacement = 'left' | 'right' | 'center';

@Component({
	selector: 'skl-drawer',
	template: `
		@if (isDrawerVisible()) {
			<aside
				#drawerPopover
				tabindex="0"
				animate.leave="drawer-out"
				[attr.data-placement]="placement()"
			>
				@if (canResize()) {
					<div
						class="resizer"
						[class]="isResizing() ? 'resize' : ''"
						(pointerdown)="startResizing($event)"
					></div>
				}
				<div class="aside-content skl-drawer">
					<ng-content />
				</div>
			</aside>

			@if (isBackdropVisible()) {
				<div
					class="backdrop"
					animate.leave="backdrop-out"
					(click)="onLightDismiss()"
				></div>
			}
		}
	`,
	styles: `
		:host {
			--surface: var(--surface-3-color);

			aside {
				display: flex;
				position: absolute;
				inset: 0;
				pointer-events: all;
				block-size: 100%;
				inline-size: var(--_drawer-resize-size, var(--drawer-width, fit-content));
				will-change: transform, opacity;
				transition: transform 0.5s cubic-bezier(0.66, 0, 0.34, 1);
				max-inline-size: 100cqw;
				background-color: var(--surface, white);
				isolation: isolate;
				z-index: 10;

				&::after {
					content: '';
					position: absolute;
					top: 0;
					width: 100%;
					height: 100%;
					box-shadow: 0 0 2rem 0.25rem oklch(0 0 0 / 24%);
					z-index: -1;
				}
				
				.resizer {
					display: flex;
					width: 12px;
					cursor: col-resize;
					touch-action: none;
					background-color: var(--surface, white);

					&::after {
						content: '';
						block-size: 3em;
						inline-size: 4px;
						border-radius: 20px;
						transition: opacity 0.2s ease-in;
					}
				
					@media (pointer: fine) {
						&:hover,
						&.resize {

							&::after {
								opacity: 1;
							}
						}
						
						&::after {
							block-size: 100%;
							inline-size: 2px;
							border-radius: 20px;
							background-color: var(--line-2-color, white);
							opacity: 0;
						}

					}

					@media (pointer: coarse) {
						align-items: center;
						justify-content: center;
						width: 24px;
						cursor: pointer;

						&.resize {
							&::after {
								background-color: oklch(from var(--line-2-color) l c h / 40%);
							}
						}

						&::after {
							block-size: 3em;
							inline-size: 4px;
							border-radius: 20px;
							background-color: var(--line-2-color, white);
						}
					}
				}

				@starting-style {
					transform: var(--placement-transform, 0);
				}

				&[data-placement="left"] {
					right: auto;
					--placement-transform: translateX(-100%);
				}

				&[data-placement="right"] {
					left: auto;
					--placement-transform: translateX(100%);
				}

				&[data-placement="center"] {
					justify-self: center;
					--placement-transform: translateY(100%);
				}
			}

			.backdrop {
				position: absolute;
				inset: 0;
				width: 100%;
				height: 100%;
				background: rgb(0 0 0 / 40%);
				pointer-events: all;
				backdrop-filter: blur(12px) saturate(180%);
				will-change: opacity;
				transition: opacity 0.3s ease-in-out;
				touch-action: none;
				outline: none;
				z-index: 1;

				@starting-style {
					opacity: 0;
				}
			}

			.backdrop-out {
				opacity: 0;
			}

			.drawer-out {
				transform: var(--placement-transform, 0);
			}
		}
	`,
	host: {
		'(document:keydown.escape)': 'onLightDismiss()',
		'(document:pointerdown)': 'onDocumentClick($event)',
		'[style.--drawer-width]': 'width()',
	},
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrawerComponent {
	readonly #ngZone = inject(NgZone);
	readonly #renderer = inject(Renderer2);
	readonly #destroyRef = inject(DestroyRef);
	#unlistenMouseMove: (() => void) | null = null;
	#unlistenMouseUp: (() => void) | null = null;

	readonly #showDrawer = signal(false);
	readonly #canClose = signal(true);

	protected readonly drawerPopover = viewChild<ElementRef<HTMLDivElement>>('drawerPopover');

	public readonly width = signal<string | undefined>(undefined);
	public readonly placement = signal<DrawerPlacement>('right');
	public readonly showBackdrop = signal(false);
	public readonly lightDismiss = signal(false);
	public readonly canResize = signal(false);
	public readonly isResizing = signal(false);
	public readonly isDrawerVisible = computed(() => this.#showDrawer());
	public readonly isBackdropVisible = computed(() => this.showBackdrop());

	readonly closed = output();

	constructor() {
		this.#destroyRef.onDestroy(() => this.#cleanupListeners());
	}

	public toggle() {
		this.#showDrawer.update(prev => !prev);
	}

	public close() {
		if (!this.#canClose() || !this.#showDrawer()) return;

		const drawerEl = this.drawerPopover()?.nativeElement;

		this.isResizing.set(false);
		this.#showDrawer.set(false);
		this.#cleanupListeners();

		if (drawerEl) {
			requestAnimationFrame(async () => {
				const animations = drawerEl.getAnimations();

				if (animations.length) {
					await Promise.allSettled(animations.map(a => a.finished));
				}

				this.closed.emit();
			});
		} else {
			this.closed.emit();
		}
	}

	public open() {
		this.#showDrawer.set(true);
		this.isResizing.set(false);
	}

	protected onLightDismiss() {
		if (!this.lightDismiss()) return;

		this.close();
	}

	protected onDocumentClick(event: PointerEvent) {
		if (!this.isDrawerVisible() || !this.lightDismiss() || this.isResizing()) return;

		const drawerElement = this.drawerPopover()?.nativeElement;

		if (drawerElement && !event.composedPath().includes(drawerElement as EventTarget)) {
			const isOverlayClick = event.composedPath().some(el =>
				el instanceof HTMLElement &&
				!drawerElement.contains(el) &&
				(/overlay|popover|modal|dialog|toast/i.test(el.tagName) ||
				/overlay|popover|modal|dialog|toast/i.test(el.classList.value))
			);

			if (isOverlayClick) return;

			this.close();
		}
	}

	protected startResizing(event: PointerEvent) {
		event.preventDefault();
		this.isResizing.set(true);

		this.#ngZone.runOutsideAngular(() => {
			this.#unlistenMouseMove = this.#renderer.listen(
				'document',
				'pointermove',
				(e: PointerEvent) => {
					this.onResizing(e);
				}
			);

			this.#unlistenMouseUp = this.#renderer.listen('document', 'pointerup', () => {
				this.stopResizing();
			});
		});
	}

	protected stopResizing() {
		this.#ngZone.run(() => {
			this.isResizing.set(false);
		});
		this.#cleanupListeners();
	}

	protected onResizing(event: PointerEvent) {
		const drawerElement = this.drawerPopover()?.nativeElement;
		if (!drawerElement) return;

		const resizeMap = {
			right: `calc(100vw - ${event.clientX}px)`,
			left: `calc(${event.clientX}px / 100vw * 100%)`,
			center: `calc((${event.clientY}px / 100vh) * 100%)`,
		} as const;

		const size = resizeMap[this.placement()];

		drawerElement.style.setProperty('--_drawer-resize-size', size);
	}

	#cleanupListeners() {
		if (this.#unlistenMouseMove) {
			this.#unlistenMouseMove();
			this.#unlistenMouseMove = null;
		}

		if (this.#unlistenMouseUp) {
			this.#unlistenMouseUp();
			this.#unlistenMouseUp = null;
		}
	}
}
